import React, { useState, useEffect } from "react";
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Chip,
  Alert, Snackbar, Tooltip, Grid, FormControl, InputLabel,
  Select, MenuItem, Tabs, Tab, LinearProgress
} from "@mui/material";
import { Add, Edit, Delete, Refresh, Business, PlaylistAdd } from "@mui/icons-material";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";
const CATEGORIAS = [
  { value: "ADMINISTRATIVO", label: "Administrativo", color: "primary" },
  { value: "VENTAS", label: "Ventas/Comercial", color: "success" },
  { value: "OPERATIVO", label: "Operativo/Productivo", color: "warning" },
];

export default function CentroCostos() {
  const [centros, setCentros] = useState([]);
  const [resumen, setResumen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [form, setForm] = useState({
    codigo: "", nombre: "", categoria: "OPERATIVO",
    descripcion: "", responsable: "", padre_id: "", presupuesto_mensual: ""
  });

  const h = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });
  const msg = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  useEffect(() => { cargar(); }, []);

  const cargar = async () => {
    setLoading(true);
    try {
      const [centrosRes, resumenRes] = await Promise.all([
        axios.get(`${API_URL}/api/centro-costos`, { headers: h() }),
        axios.get(`${API_URL}/api/centro-costos/resumen`, { headers: h() }).catch(() => ({ data: [] }))
      ]);
      setCentros(centrosRes.data);
      setResumen(resumenRes.data);
    } catch { msg("Error al cargar", "error"); }
    finally { setLoading(false); }
  };

  const inicializar = async () => {
    if (!window.confirm("Esto creará el catálogo base de centros de costos. ¿Continuar?")) return;
    try {
      const res = await axios.post(`${API_URL}/api/centro-costos/inicializar`, {}, { headers: h() });
      msg(res.data.message);
      cargar();
    } catch (e) { msg(e.response?.data?.error || "Error", "error"); }
  };

  const guardar = async () => {
    try {
      const payload = { ...form, presupuesto_mensual: parseFloat(form.presupuesto_mensual || 0) };
      if (!payload.padre_id) delete payload.padre_id;
      if (editando) {
        await axios.put(`${API_URL}/api/centro-costos/${editando}`, payload, { headers: h() });
      } else {
        await axios.post(`${API_URL}/api/centro-costos`, payload, { headers: h() });
      }
      setDialogOpen(false);
      setEditando(null);
      cargar();
      msg("Guardado correctamente");
    } catch (e) { msg(e.response?.data?.error || "Error", "error"); }
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Desactivar este centro de costos?")) return;
    try {
      await axios.delete(`${API_URL}/api/centro-costos/${id}`, { headers: h() });
      cargar();
      msg("Desactivado");
    } catch (e) { msg(e.response?.data?.error || "Error", "error"); }
  };

  const abrirNuevo = (padreId = "") => {
    setEditando(null);
    setForm({ codigo: "", nombre: "", categoria: "OPERATIVO", descripcion: "", responsable: "", padre_id: padreId, presupuesto_mensual: "" });
    setDialogOpen(true);
  };

  const abrirEditar = (cc) => {
    setEditando(cc.id);
    setForm({ codigo: cc.codigo, nombre: cc.nombre, categoria: cc.categoria, descripcion: cc.descripcion || "", responsable: cc.responsable || "", padre_id: cc.padre_id || "", presupuesto_mensual: cc.presupuesto_mensual || "" });
    setDialogOpen(true);
  };

  const padres = centros.filter(c => !c.padre_id && c.activo);
  const getHijos = (padreId) => centros.filter(c => c.padre_id === padreId && c.activo);
  const getCatColor = (cat) => CATEGORIAS.find(c => c.value === cat)?.color || "default";
  const getResumenCC = (id) => resumen.find(r => r.id === id);
  const fmt = (n) => parseFloat(n || 0).toLocaleString("es-EC", { minimumFractionDigits: 2 });

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Business color="primary" />
            <Typography variant="h5" fontWeight={700}>Centro de Costos</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            {centros.length === 0 && (
              <Button startIcon={<PlaylistAdd />} onClick={inicializar} variant="contained" color="success">
                Inicializar Catálogo
              </Button>
            )}
            <Button startIcon={<Refresh />} onClick={cargar} variant="outlined">Actualizar</Button>
            <Button startIcon={<Add />} onClick={() => abrirNuevo()} variant="contained">Nuevo</Button>
          </Box>
        </Box>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Catálogo" />
          <Tab label="Resumen Financiero" />
        </Tabs>

        {/* Tab Catálogo */}
        {tab === 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell><strong>Código</strong></TableCell>
                  <TableCell><strong>Nombre</strong></TableCell>
                  <TableCell><strong>Categoría</strong></TableCell>
                  <TableCell><strong>Responsable</strong></TableCell>
                  <TableCell align="right"><strong>Presupuesto</strong></TableCell>
                  <TableCell align="center"><strong>Acciones</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {padres.map(padre => (
                  <React.Fragment key={padre.id}>
                    <TableRow hover sx={{ bgcolor: "#fafafa" }}>
                      <TableCell><strong>{padre.codigo}</strong></TableCell>
                      <TableCell><strong>{padre.nombre}</strong></TableCell>
                      <TableCell><Chip label={padre.categoria} color={getCatColor(padre.categoria)} size="small" /></TableCell>
                      <TableCell>{padre.responsable}</TableCell>
                      <TableCell align="right">{padre.presupuesto_mensual > 0 ? `$${fmt(padre.presupuesto_mensual)}` : "—"}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Agregar subcentro"><IconButton size="small" color="primary" onClick={() => abrirNuevo(padre.id)}><Add /></IconButton></Tooltip>
                        <Tooltip title="Editar"><IconButton size="small" onClick={() => abrirEditar(padre)}><Edit /></IconButton></Tooltip>
                        <Tooltip title="Desactivar"><IconButton size="small" color="error" onClick={() => eliminar(padre.id)}><Delete /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                    {getHijos(padre.id).map(hijo => (
                      <TableRow key={hijo.id} hover>
                        <TableCell sx={{ pl: 4 }}>{hijo.codigo}</TableCell>
                        <TableCell sx={{ pl: 4 }}>↳ {hijo.nombre}</TableCell>
                        <TableCell><Chip label={hijo.categoria} color={getCatColor(hijo.categoria)} size="small" variant="outlined" /></TableCell>
                        <TableCell>{hijo.responsable}</TableCell>
                        <TableCell align="right">{hijo.presupuesto_mensual > 0 ? `$${fmt(hijo.presupuesto_mensual)}` : "—"}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Editar"><IconButton size="small" onClick={() => abrirEditar(hijo)}><Edit /></IconButton></Tooltip>
                          <Tooltip title="Desactivar"><IconButton size="small" color="error" onClick={() => eliminar(hijo.id)}><Delete /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
                {centros.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Alert severity="info">No hay centros de costos. Haz clic en "Inicializar Catálogo" para crear la estructura base.</Alert>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Tab Resumen Financiero */}
        {tab === 1 && (
          <Box>
            {resumen.length === 0 ? (
              <Alert severity="info">No hay movimientos registrados en centros de costos.</Alert>
            ) : (
              <Grid container spacing={2}>
                {CATEGORIAS.map(cat => {
                  const centrosCat = resumen.filter(r => r.categoria === cat.value && r.movimientos > 0);
                  if (centrosCat.length === 0) return null;
                  return (
                    <Grid item xs={12} md={4} key={cat.value}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" color={`${cat.color}.main`} gutterBottom>{cat.label}</Typography>
                        {centrosCat.map(cc => (
                          <Box key={cc.id} sx={{ mb: 2 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                              <Typography variant="body2"><strong>{cc.nombre}</strong></Typography>
                              <Typography variant="body2">${fmt(cc.saldo)}</Typography>
                            </Box>
                            {cc.presupuesto_mensual > 0 && (
                              <Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min((parseFloat(cc.saldo) / cc.presupuesto_mensual) * 100, 100)}
                                  color={parseFloat(cc.saldo) > cc.presupuesto_mensual ? "error" : "primary"}
                                  sx={{ mt: 0.5 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {cc.porcentaje_presupuesto} del presupuesto (${fmt(cc.presupuesto_mensual)})
                                </Typography>
                              </Box>
                            )}
                            <Typography variant="caption" color="text.secondary">{cc.movimientos} movimientos</Typography>
                          </Box>
                        ))}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        )}
      </Paper>

      {/* Dialog Crear/Editar */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editando ? "Editar" : "Nuevo"} Centro de Costos</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <TextField fullWidth label="Código" value={form.codigo} onChange={e => setForm({ ...form, codigo: e.target.value })} disabled={!!editando} required />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Categoría</InputLabel>
                <Select value={form.categoria} label="Categoría" onChange={e => setForm({ ...form, categoria: e.target.value })}>
                  {CATEGORIAS.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Nombre" value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} required />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Centro Padre (opcional)</InputLabel>
                <Select value={form.padre_id} label="Centro Padre (opcional)" onChange={e => setForm({ ...form, padre_id: e.target.value })}>
                  <MenuItem value="">Ninguno (es principal)</MenuItem>
                  {padres.map(p => <MenuItem key={p.id} value={p.id}>{p.codigo} - {p.nombre}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Responsable" value={form.responsable} onChange={e => setForm({ ...form, responsable: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Presupuesto Mensual" type="number" value={form.presupuesto_mensual} onChange={e => setForm({ ...form, presupuesto_mensual: e.target.value })} inputProps={{ step: "0.01", min: "0" }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Descripción" value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button onClick={guardar} variant="contained" disabled={!form.codigo || !form.nombre}>Guardar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
